import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import './Auth.css';
import './CharacterSelection.css';
import './Village.css';
import './Inventory.css';
import './Battle.css';
import { authButtonSmallStyle, authButtonStyle } from '../constants/authStyles';
import AuthLayeredCard from './AuthLayeredCard';
import AuthSelectionToolbar from './AuthSelectionToolbar';
import { TravelPanel } from './Travel';
import BlacksmithWorkbench from './BlacksmithWorkbench';
import NpcShopWorkbench from './NpcShopWorkbench';
import StoryNpcTasks, { storyNpcTaskNames, useStoryView } from './StoryNpcTasks';
import { useCharacter } from '../context/CharacterContext';
import { usePopupMessage } from '../hooks/usePopupMessage';
import NpcDialogue, { NpcServiceStrip } from './npc/NpcDialogue';
import NpcPortrait, { npcSceneUrl } from './npc/NpcPortrait';
import { tl } from '../utils/inlineI18n';
import { translateGameName } from '../utils/gameNameI18n';
import { ProfessionTrainer, PROFESSION_TRAINER_NPCS } from './ProfessionPanels';
import Coachmark from './tutorial/Coachmark';

const SuccessPopup = ({ message, onClose }) => {
  const { t: tc } = useTranslation('common');
  return (
  <div className="popup-overlay" onClick={onClose}>
    <AuthLayeredCard
      className="popup-card"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="auth-header popup-header">
        <h3>{tc('success')}</h3>
      </div>
      <div className="popup-body">
        <p>{message}</p>
      </div>
      <div className="popup-footer">
        <button
          type="button"
          className="auth-button village-button--full"
          style={authButtonStyle}
          onClick={onClose}
        >
          {tc('ok')}
        </button>
      </div>
    </AuthLayeredCard>
  </div>
  );
};

const Village = () => {
  const { t } = useTranslation('village');
  const { t: tc } = useTranslation('common');
  const { character: userCharacter, refreshCharacter } = useCharacter();
  const [npcs, setNpcs] = useState([]);
  const [selectedNPC, setSelectedNPC] = useState(null);
  // 'dialogue': sinematik konuşma ekranı, 'service': dükkan / demirci tezgâhı
  const [npcStage, setNpcStage] = useState('dialogue');
  const { setError, clearError } = usePopupMessage();
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedTab = searchParams.get('tab') === 'travel' ? 'travel' : 'village';
  const [activeTab, setActiveTab] = useState(requestedTab);
  const username = localStorage.getItem('username');
  const userGold = userCharacter?.gold || 0;
  const story = useStoryView(username);
  const npcTaskNames = storyNpcTaskNames(story, userCharacter?.villageId);

  const loadNpcs = async (villageId) => {
    try {
      const npcsResponse = await axios.get(`/api/npcs?villageId=${villageId}`);
      if (npcsResponse.data.success && npcsResponse.data.npcs) {
        setNpcs(npcsResponse.data.npcs);
      } else {
        setNpcs([]);
      }
    } catch (npcsErr) {
      console.error('Error fetching NPCs:', npcsErr);
      setNpcs([]);
    }
  };

  useEffect(() => {
    if (!username) {
      navigate('/login');
      return;
    }
    if (!userCharacter) {
      return;
    }
    loadNpcs(userCharacter.villageId);
  }, [username, navigate, userCharacter]);

  useEffect(() => {
    setActiveTab(requestedTab);
  }, [requestedTab]);

  const handleNPCClick = (npc) => {
    clearError();
    setSelectedNPC(npc);
    setNpcStage('dialogue');
  };

  const handleDialogueClose = () => {
    setSelectedNPC(null);
    setNpcStage('dialogue');
  };

  const handleBack = () => {
    if (selectedNPC) {
      clearError();
      setSelectedNPC(null);
    } else {
      navigate('/game');
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setSearchParams(tab === 'travel' ? { tab: 'travel' } : {});
  };

  const handleShopBuySuccess = (itemName) => {
    setSuccessMessage(t('shopBuySuccess', { item: itemName || tc('unknownItem') }));
    setShowSuccessPopup(true);
  };

  // NPC detay ekranı
  if (selectedNPC && (npcStage === 'service' || npcStage === 'training')) {
    return (
      <>
        {showSuccessPopup && (
          <SuccessPopup
            message={successMessage}
            onClose={() => setShowSuccessPopup(false)}
          />
        )}
        <div className={`auth-container auth-container--selection${selectedNPC.tip === 1 ? ' auth-container--blacksmith' : ''}${selectedNPC.tip === 2 ? ' auth-container--npc-shop' : ''}`}>
          <AuthLayeredCard>
            <AuthSelectionToolbar
              onBack={handleBack}
              buttonStyle={authButtonStyle}
              header={
                <>
                  {selectedNPC.tip === 1 ? (
                    <div className="npc-header-inline">
                      <h1>{selectedNPC.name}</h1>
                      <p className="npc-header-desc">{selectedNPC.description}</p>
                    </div>
                  ) : (
                    <>
                      <h1>{selectedNPC.name}</h1>
                      <p>{selectedNPC.description}</p>
                    </>
                  )}
                  {selectedNPC.tip === 2 && (
                    <div className="user-gold">
                      <strong>{tc('yang')}: {userGold}</strong>
                    </div>
                  )}
                </>
              }
            />
            <div className="auth-selection-body npc-detail">
            <div className="npc-content">
              <NpcServiceStrip
                key={selectedNPC.id}
                npc={selectedNPC}
                onTalk={() => setNpcStage('dialogue')}
              />
              <StoryNpcTasks
                npcName={selectedNPC.name}
                villageId={userCharacter?.villageId}
                username={username}
                onCharacterChange={() => refreshCharacter?.()}
              />
              {npcStage === 'training' ? (
                <ProfessionTrainer npcName={selectedNPC.name} onGoldChange={() => refreshCharacter?.()} />
              ) : selectedNPC.tip === 1 ? (
                <BlacksmithWorkbench
                  username={username}
                  userCharacter={userCharacter}
                  userGold={userGold}
                  refreshCharacter={refreshCharacter}
                  setError={setError}
                  clearError={clearError}
                />
              ) : selectedNPC.tip === 2 ? (
                <NpcShopWorkbench
                  npcId={selectedNPC.id}
                  username={username}
                  userGold={userGold}
                  refreshCharacter={refreshCharacter}
                  setError={setError}
                  clearError={clearError}
                  onBuySuccess={handleShopBuySuccess}
                />
              ) : (
                <p className="coming-soon">{tc('comingSoon')}</p>
              )}
            </div>
          </div>
        </AuthLayeredCard>
      </div>
      </>
    );
  }

  // Ana köy ekranı
  return (
    <>
      {showSuccessPopup && (
        <SuccessPopup
          message={successMessage}
          onClose={() => setShowSuccessPopup(false)}
        />
      )}
      <div className="auth-container auth-container--selection">
        <AuthLayeredCard>
          <div className="battle-nav-row">
            <button
              type="button"
              className="auth-button auth-button--compact battle-nav-btn battle-nav-btn--back"
              style={authButtonSmallStyle}
              onClick={handleBack}
              aria-label={tc('back')}
            >
              ←
            </button>
            <button
              type="button"
              className={`auth-button auth-button--compact battle-nav-btn${activeTab === 'village' ? ' battle-subtab--active' : ''}`}
              style={authButtonSmallStyle}
              onClick={() => handleTabChange('village')}
            >
              {t('tabsVillage')}
            </button>
            <button
              type="button"
              className={`auth-button auth-button--compact battle-nav-btn${activeTab === 'travel' ? ' battle-subtab--active' : ''}`}
              style={authButtonSmallStyle}
              onClick={() => handleTabChange('travel')}
              data-tour="village-travel"
            >
              {t('tabsTravel')}
            </button>
          </div>

          {activeTab === 'village' ? (
            <div className="auth-selection-body">
              <Coachmark
                tourId="village"
                enabled={activeTab === 'village'}
                steps={[
                  { selector: '.village-npc-grid', textKey: 'villageNpcs' },
                  { selector: '[data-tour="village-travel"]', textKey: 'villageTravel' },
                ]}
              />
              <div className="selection-grid village-npc-grid">
                {npcs && npcs.length > 0 ? (
                  npcs.map((npc) => (
                    <div
                      key={npc.id}
                      className={`selection-card${npcTaskNames.has(npc.name) ? ' sq-npc-card' : ''}`}
                      title={npc.name}
                      onClick={() => handleNPCClick(npc)}
                    >
                      {npcTaskNames.has(npc.name) ? (
                        <span className="sq-npc-badge" title={tl('Hikâye görevi var', 'Story quest available')}>!</span>
                      ) : null}
                      <div className="selection-card__media">
                        {npc.imageUrl ? (
                          <img src={npcSceneUrl(npc)} alt={npc.name} />
                        ) : (
                          <NpcPortrait npc={npc} viewBox="40 60 220 220" />
                        )}
                      </div>
                      <h3>{translateGameName(npc.name)}</h3>
                      <p>{npc.description}</p>
                    </div>
                  ))
                ) : (
                  <div className="empty-npcs">
                    <p>{t('npcNone')}</p>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <TravelPanel />
          )}
        </AuthLayeredCard>
      </div>
      {selectedNPC && npcStage === 'dialogue' ? (
        <NpcDialogue
          key={selectedNPC.id}
          npc={selectedNPC}
          hasTask={npcTaskNames.has(selectedNPC.name)}
          canTrain={PROFESSION_TRAINER_NPCS.has(selectedNPC.name)}
          onTrain={() => setNpcStage('training')}
          villageId={userCharacter?.villageId}
          username={username}
          onService={() => setNpcStage('service')}
          onClose={handleDialogueClose}
          onCharacterChange={() => refreshCharacter?.()}
        />
      ) : null}
      </>
    );
  }

export default Village;

